import {
  HubConnectionBuilder,
  LogLevel,
  RetryContext,
} from '@microsoft/signalr'
import { APIS, ENV, LOADERS } from '../config/constants'
import { useLoader } from 'src/hooks/useLoader'
import { useSignalRStore } from 'src/stores'
import { useClientContextStore } from 'src/client/stores'
import { useAuthStore } from 'src/common/store'
import { signalRBus } from '../signalR/signalRBus'
import { registerSignalRHandlers } from '../signalR'
import { SIGNAL_R_SCOPES, SystemResponse } from 'src/signalR/types'
import { storeToRefs } from 'pinia'

export const useSignalR = () => {
  const signalRStore = useSignalRStore()
  const {
    orgConnection,
    userConnection,
    isUserConnected,
    isOrgConnected,
    isOrgDisconnecting,
    isUserDisconnecting,
  } = storeToRefs(signalRStore)
  const { updateOrgConnection, updateUserConnection } = signalRStore

  const authStore = useAuthStore()
  const clientContextStore = useClientContextStore()

  async function initializeConnections() {
    if (!signalRStore.handlersRegistered) {
      signalRStore.handlersRegistered = true
      registerSignalRHandlers()
    }
    await initializeOrgConnection()
    await initializeUserConnection()
  }

  async function initializeOrgConnection() {
    try {
      await stopOrgConnection()

      const { usePostLoader: getOrgConnectionInfo } = useLoader(
        LOADERS.signalR,
        { uri: APIS.negotiate },
      )
      const orgConnectionData = await getOrgConnectionInfo({})
      const { accessToken: orgAccessToken, url: orgUrl } =
        orgConnectionData || {}
      const orgOptions = { accessTokenFactory: () => orgAccessToken }

      const connection = new HubConnectionBuilder()
        .withUrl(orgUrl, orgOptions)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: calculateRetry,
        })
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      connection?.on(SIGNAL_R_SCOPES.system, (res: SystemResponse) => {
        signalRBus.emit(SIGNAL_R_SCOPES.system, res)
      })
      connection?.onreconnected(() => {
        signalRBus.emit(SIGNAL_R_SCOPES.system, null, { reconnect: true })
      })
      await connection.start()
      signalRBus.emit(SIGNAL_R_SCOPES.system, null, { reconnect: true })
      updateOrgConnection(connection)
    } catch {
      await stopOrgConnection()
    }
  }

  async function initializeUserConnection() {
    try {
      await stopUserConnection()

      const { usePostLoader: getUserConnectionInfo } = useLoader(
        LOADERS.signalR,
        { uri: `${APIS.negotiate}/${APIS.user}` },
      )
      const userConnectionData = await getUserConnectionInfo({})
      const { accessToken: userAccessToken, url: userUrl } =
        userConnectionData || {}
      const userOptions = { accessTokenFactory: () => userAccessToken }

      const connection = new HubConnectionBuilder()
        .withUrl(userUrl, userOptions)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: calculateRetry,
        })
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      const email = authStore.user?.email
      const orgId = authStore.currentOrgId
      const centerId = authStore.currentCenterId
      const familyId = clientContextStore.family?.id
      const role = authStore.role
      const availableCenters = authStore.availableCenters || []

      const roleGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `role:${orgId}:${center}:${role}`)
          : authStore.isOrgAdmin
            ? [`role:${orgId}::${role}`]
            : [`role:${orgId}:${centerId}:${role}`]

      const centerGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `center:${center}`)
          : centerId
            ? [`center:${centerId}`]
            : []

      const userGroups = [
        `user:${email}`,
        ...roleGroups,
        ...centerGroups,
        ...(familyId ? [`family:${familyId}`] : []),
      ]

      await Promise.allSettled(
        userGroups.map(async (group) => {
          try {
            const { usePostLoader: joinGroup } = useLoader(LOADERS.signalR, {
              uri: `${APIS.groups}/${APIS.add}`,
            })
            await joinGroup({ groupName: group })
          } catch {
            // Silently handle group join failures
          }
        }),
      )

      connection?.on(
        SIGNAL_R_SCOPES.notification,
        async (res: SystemResponse) => {
          signalRBus.emit(SIGNAL_R_SCOPES.notification, res)
        },
      )

      connection?.on(SIGNAL_R_SCOPES.message, (res: SystemResponse) => {
        signalRBus.emit(SIGNAL_R_SCOPES.message, res)
      })
      connection?.onreconnected(() => {
        signalRBus.emit(SIGNAL_R_SCOPES.message, null, { reconnect: true })
      })
      await connection?.start()
      signalRBus.emit(SIGNAL_R_SCOPES.message, null, { reconnect: true })
      updateUserConnection(connection)
    } catch {
      await stopUserConnection()
    }
  }

  function reconnectIfNeeded() {
    if (isOrgDisconnecting.value || isUserDisconnecting.value) {
      setTimeout(reconnectIfNeeded, 3000)
      return
    }
    if (!isOrgConnected.value) {
      void initializeOrgConnection()
    }
    if (!isUserConnected.value) {
      void initializeUserConnection()
    }
  }

  async function stopConnections() {
    await Promise.all([stopOrgConnection(), stopUserConnection()])
  }

  async function stopOrgConnection() {
    if (!orgConnection.value) return
    await orgConnection.value.stop()
  }

  async function stopUserConnection() {
    if (!userConnection.value) return
    if (isUserConnected.value) {
      const userId = authStore.user?.id
      const orgId = authStore.currentOrgId
      const centerId = authStore.currentCenterId
      const familyId = clientContextStore.family?.id
      const role = authStore.user?.role
      const availableCenters = authStore.availableCenters || []

      if (userId) {
        const roleGroups =
          availableCenters.length > 0
            ? availableCenters.map(
                (center) => `role:${orgId}:${center}:${role}`,
              )
            : authStore.isOrgAdmin
              ? [`role:${orgId}::${role}`]
              : [`role:${orgId}:${centerId}:${role}`]

        const centerGroups =
          availableCenters.length > 0
            ? availableCenters.map((center) => `center:${center}`)
            : centerId
              ? [`center:${centerId}`]
              : []

        const userGroups = [
          `user:${userId}`,
          ...roleGroups,
          ...centerGroups,
          ...(familyId ? [`family:${familyId}`] : []),
        ]

        void Promise.allSettled(
          userGroups.map(async (group) => {
            try {
              const { useDeleteBulk: leaveGroup } = useLoader(LOADERS.signalR, {
                uri: `${APIS.groups}/${APIS.remove}`,
              })
              await leaveGroup({
                groupName: group,
                userId: userId,
              })
            } catch {
              // Silently handle cleanup failures
            }
          }),
        )
      }
    }
    await userConnection.value.stop()
  }

  function calculateRetry(context: RetryContext) {
    if (context.previousRetryCount >= 20) {
      return null
    }
    return getDelay(context)
  }

  function getDelay(context: RetryContext) {
    if (context.previousRetryCount < 2) return 2000
    if (context.previousRetryCount < 5) return 5000
    if (context.previousRetryCount < 10) return 30000
    return 60000
  }

  return {
    initializeConnections,
    reconnectIfNeeded,
    stopConnections,
  }
}
